import { Paper, Text, Box, Modal, Stack, Badge, Divider, Group, useMantineTheme, Image, SimpleGrid, Title, Container } from '@mantine/core';
import { IconUser, IconUsers, IconMapPin, IconClipboardList, IconCalendar } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { pb, Audit, IstZustandMassnahme, Auditor, Kontrollpunkt } from '../../lib/pocketbase';
import dayjs from 'dayjs';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import deLocale from '@fullcalendar/core/locales/de';

interface AuditInfo {
  id: string;
  auditnummer: string;
  auditdatum: string;
  dienststelle: {
    name: string;
  };
  status: string;
  auditors: Auditor[];
  begleitpersonen: string;
  kontrollpunkt: Kontrollpunkt[];
  massnahmen: IstZustandMassnahme[];
}

const ImageThumbnail = ({ massnahme }: { massnahme: IstZustandMassnahme }) => {
  if (!massnahme.anhaenge?.length) return null;
  
  const thumbnailUrl = `${pb.baseUrl}/api/files/${massnahme.collectionId}/${massnahme.id}/${massnahme.anhaenge[0]}?thumb=100x100`;
  
  return (
    <Box
      style={{
        position: 'relative',
        borderRadius: '8px',
        overflow: 'hidden',
        transition: 'transform 0.2s ease',
        '&:hover': {
          transform: 'scale(1.05)',
        },
      }}
    >
      <Image
        src={thumbnailUrl}
        height={40}
        width={40}
        radius="md"
        fit="cover"
        style={{
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}
      />
      <Badge
        variant="filled"
        size="md"
        style={{
          position: 'absolute',
          bottom: 4,
          right: 4,
          zIndex: 2,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          backdropFilter: 'blur(4px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          padding: '2px 4px',
          minWidth: 'unset',
          fontSize: '10px',
        }}
      >
        {massnahme.nummer}
      </Badge>
    </Box>
  );
};

const AuditorsList = ({ auditors }: { auditors: Auditor[] }) => {
  if (!auditors.length) {
    return (
      <Text size="xs" c="dimmed">
        Keine Auditoren
      </Text>
    );
  }

  const displayText = auditors.length === 1
    ? `${auditors[0].vorname} ${auditors[0].name}`
    : `${auditors[0].vorname} ${auditors[0].name} +${auditors.length - 1}`;

  return (
    <Text size="xs" title={auditors.map(a => `${a.vorname} ${a.name}`).join(', ')}>
      {displayText}
    </Text>
  );
};

export function Kalender() {
  const [auditsByDate, setAuditsByDate] = useState<{ [key: string]: AuditInfo[] }>({});
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const theme = useMantineTheme();

  const fetchMassnahmen = async (auditId: string): Promise<IstZustandMassnahme[]> => {
    try {
      const result = await pb.collection('ist_zustand_massnahmen').getList(1, 3, {
        filter: `audit = "${auditId}"`,
        sort: '-created',
      });
      return result.items as IstZustandMassnahme[];
    } catch (error) {
      console.error(`Error fetching massnahmen for audit ${auditId}:`, error);
      return [];
    }
  };

  useEffect(() => {
    const fetchAudits = async () => {
      try {
        const records = await pb.collection('audits').getFullList<Audit>({
          expand: 'dienststelle,auditor,kontrollpunkt',
          sort: '-created',
        });

        const auditsWithMassnahmen = await Promise.all(
          records.map(async (record) => {
            const massnahmen = await fetchMassnahmen(record.id);
            const auditors = record.expand?.auditor ? 
              (Array.isArray(record.expand.auditor) ? record.expand.auditor : [record.expand.auditor]) 
              : [];
            const kontrollpunkt = record.expand?.kontrollpunkt || [];
            
            return {
              id: record.id,
              auditnummer: record.auditnummer,
              auditdatum: record.auditdatum,
              dienststelle: {
                name: record.expand?.dienststelle?.name || 'Keine Dienststelle'
              },
              status: record.status,
              auditors,
              begleitpersonen: record.begleitpersonen || '',
              kontrollpunkt,
              massnahmen
            } as AuditInfo;
          })
        );

        const grouped = auditsWithMassnahmen.reduce((acc: { [key: string]: AuditInfo[] }, audit) => {
          if (audit.auditdatum) {
            const date = dayjs(audit.auditdatum).format('YYYY-MM-DD');
            if (!acc[date]) {
              acc[date] = [];
            }
            acc[date].push(audit);
          }
          return acc;
        }, {});

        setAuditsByDate(grouped);
      } catch (error) {
        console.error('Error fetching audits:', error);
      }
    };

    fetchAudits();
  }, []);

  const getStatusColor = (status: string) => {
    switch (status.toUpperCase()) {
      case 'GEPLANT':
        return theme.colors.red[6];
      case 'IN_BEARBEITUNG':
        return theme.colors.yellow[6];
      case 'ABGESCHLOSSEN':
        return theme.colors.green[6];
      default:
        return theme.colors.gray[6];
    }
  };

  const handleDateClick = (arg: { date: Date }) => {
    setSelectedDate(arg.date);
  };

  const getCalendarEvents = () => {
    return Object.entries(auditsByDate).flatMap(([date, audits]) =>
      audits.map(audit => ({
        title: `Audit ${audit.auditnummer}`,
        date: date,
        backgroundColor: 'transparent',
        borderColor: 'transparent',
        extendedProps: {
          auditInfo: audit
        }
      }))
    );
  };

  const EventContent = ({ audit }: { audit: AuditInfo }) => {
    const massnahmenWithAttachments = audit.massnahmen
      .filter(m => m.anhaenge && m.anhaenge.length > 0);

    return (
      <Box 
        p="xs"
        style={{ 
          backgroundColor: theme.white,
          borderRadius: theme.radius.sm,
          border: `1px solid ${theme.colors.red[3]}`,
          height: '100%'
        }}
      >
        <Group justify="space-between" mb={8}>
          <Badge size="sm" variant="filled" color="red">
            #{audit.auditnummer}
          </Badge>
        </Group>

        <Group gap={4} mb={8}>
          <IconUser size={14} color={theme.colors.gray[6]} />
          <AuditorsList auditors={audit.auditors} />
        </Group>

        {audit.begleitpersonen && (
          <Group gap={4} mb={8}>
            <IconUsers size={14} color={theme.colors.gray[6]} />
            <Text size="xs" c={theme.colors.gray[7]} lineClamp={1}>
              {audit.begleitpersonen}
            </Text>
          </Group>
        )}

        <Group gap={4} mb={8}>
          <IconMapPin size={14} color={theme.colors.gray[6]} />
          <Text size="xs" c={theme.colors.gray[7]} lineClamp={1}>
            {audit.dienststelle.name}
          </Text>
        </Group>

        <Group gap={4} mb={8}>
          <IconClipboardList size={14} color={theme.colors.gray[6]} />
          <Text size="xs" c={theme.colors.gray[7]}>
            {audit.massnahmen.length} Massnahme(n)
          </Text>
        </Group>

        {massnahmenWithAttachments.length > 0 && (
          <SimpleGrid cols={3} spacing={4}>
            {massnahmenWithAttachments.map((massnahme) => (
              <ImageThumbnail key={massnahme.id} massnahme={massnahme} />
            ))}
          </SimpleGrid>
        )}
      </Box>
    );
  };

  return (
    <Container size="xl" py="xl">
      <Group mb="xl" gap="xs">
        <IconCalendar size={24} style={{ color: 'var(--mantine-color-blue-6)' }} />
        <Title order={2}>Kalender</Title>
      </Group>

      <Paper shadow="sm" p="md" withBorder>
        <FullCalendar
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          locale={deLocale}
          headerToolbar={{
            left: 'prev,next today',
            center: 'title',
            right: 'dayGridMonth,timeGridWeek'
          }}
          events={getCalendarEvents()}
          dateClick={handleDateClick}
          height="auto"
          eventDisplay="block"
          displayEventTime={false}
          eventContent={(eventInfo) => (
            <EventContent audit={eventInfo.event.extendedProps.auditInfo} />
          )}
          dayCellDidMount={(arg) => {
            arg.el.style.height = '180px';
          }}
        />
      </Paper>

      <Modal
        opened={!!selectedDate}
        onClose={() => setSelectedDate(null)}
        title={selectedDate ? `Audits am ${dayjs(selectedDate).format('DD.MM.YYYY')}` : ''}
        size="lg"
      >
        {selectedDate && (
          <Box>
            {(auditsByDate[dayjs(selectedDate).format('YYYY-MM-DD')] || []).map((audit) => (
              <Paper key={audit.id} p="md" mb="md" withBorder>
                <Stack gap="xs">
                  <Box mb="xs">
                    <Group justify="space-between">
                      <Badge size="lg" variant="filled" color="red">
                        #{audit.auditnummer}
                      </Badge>
                      <Badge color={getStatusColor(audit.status)}>
                        {audit.status}
                      </Badge>
                    </Group>
                  </Box>

                  <Group gap="lg">
                    <Group gap="xs">
                        <IconUser size={16} color={theme.colors.gray[6]} />
                        <Text size="sm" fw={500} c={theme.colors.gray[7]}>{(audit.auditors?.length ?? 0) === 1 ? 'Auditor:' : 'Auditoren:'}</Text>
                        <AuditorsList auditors={audit.auditors} />
                      </Group>

                    {audit.begleitpersonen && (
                      <Group gap="xs">
                        <IconUsers size={16} color={theme.colors.gray[6]} />
                        <Text size="sm" fw={500} c={theme.colors.gray[7]}>Begleitpersonen:</Text>
                        <Text size="sm" c={theme.colors.gray[7]}>{audit.begleitpersonen}</Text>
                      </Group>
                    )}
                  </Group>
                  
                  <Group gap="xs">
                    <IconMapPin size={16} color={theme.colors.gray[6]} />
                    <Text size="sm" fw={500} c={theme.colors.gray[7]}>Standort:</Text>
                    <Text size="sm" c={theme.colors.gray[7]}>{audit.dienststelle.name}</Text>
                  </Group>

                  <Group gap="xs">
                    <IconClipboardList size={16} color={theme.colors.gray[6]} />
                    <Text size="sm" fw={500} c={theme.colors.gray[7]}>{audit.massnahmen.length === 1 ? 'Massnahme:' : 'Massnahmen:'}</Text>
                    <Text size="sm" c={theme.colors.gray[7]}>{audit.massnahmen.length}</Text>
                  </Group>

                  {audit.massnahmen.length > 0 && (
                    <SimpleGrid cols={3} spacing={4}>
                      {audit.massnahmen
                        .filter(m => m.anhaenge && m.anhaenge.length > 0)
                        .map((massnahme) => (
                          <ImageThumbnail key={massnahme.id} massnahme={massnahme} />
                        ))}
                    </SimpleGrid>
                  )}

                  <Divider 
                    my="sm" 
                    label={
                      <Group gap="xs">
                        <IconClipboardList size={16} />
                        <Text fw={500}>Kontrollstellen</Text>
                      </Group>
                    } 
                    labelPosition="center"
                  />
                  
                  {audit.kontrollpunkt.length > 0 ? (
                    <Stack gap="xs">
                      {audit.kontrollpunkt.map((punkt, index) => (
                        <Box key={index}>
                          <Text size="sm" fw={500} c={theme.colors.gray[7]}>{punkt.sicherheitssystem}</Text>
                        </Box>
                      ))}
                    </Stack>
                  ) : (
                    <Text size="sm" c={theme.colors.gray[6]} ta="center">Keine Kontrollstellen definiert</Text>
                  )}
                </Stack>
              </Paper>
            ))}
            {!auditsByDate[dayjs(selectedDate).format('YYYY-MM-DD')] && (
              <Text c={theme.colors.gray[7]}>Keine Audits an diesem Tag</Text>
            )}
          </Box>
        )}
      </Modal>
    </Container>
  );
}
